import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AuthShell, FeatIcon, SvgSend,
} from "./AuthShell.js";
import { useAuth } from "../../lib/auth-context.js";

const SvgArrowLeft = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M4 12h16M11 6l-6 6 6 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * /forgot — start a password reset.
 *
 * The API issues a one-time login OTP via /auth/forgot-password
 * (purpose: login). The user enters the code at /verify, gets a
 * session, then /reset?new=1 picks up the authed setPassword path.
 *
 * Doesn't leak existence — the API always responds pending.
 */
export const Forgot = () => {
  const navigate = useNavigate();
  const { requestPasswordReset } = useAuth();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true); setError(null);
    const r = await requestPasswordReset(email);
    setSubmitting(false);
    if (!r.ok) return setError(r.message);
    sessionStorage.setItem("wo.auth.email", email.trim());
    if (r.devOtp) sessionStorage.setItem("wo.auth.devOtp", r.devOtp);
    navigate("/check-email?from=reset");
  };

  return (
    <AuthShell
      brand={{
        eyebrow: "It's okay",
        quote: <>Names, codes, where you left the keys — <em>forgetting</em> is part of being human.</>,
        cite: "— On forgetting",
        features: [
          { icon: FeatIcon.mail, label: "Sent to the email on file" },
          { icon: FeatIcon.clock, label: "Code expires in 10 minutes" },
        ],
      }}
      topRight={<Link to="/sign-in" className="btn-ghost">← Back to sign in</Link>}
      footer={<>Still stuck? <a href="mailto:hello@wiseowltalks.com">Write to a human →</a></>}
    >
      <div className="eyebrow">Account recovery</div>
      <h1 className="wo-title" style={{ marginTop: 6 }}>
        Forgot password?<br />That&apos;s alright.
      </h1>
      <p className="wo-sub">
        Tell us the email you used to sign up. We&apos;ll send a 6-digit code; once you enter it, you can set a new password.
      </p>

      <form onSubmit={onSubmit}>
        <div className="field">
          <label className="field-label" htmlFor="fp-email">Email</label>
          <input
            id="fp-email" className="field-input" type="email"
            placeholder="you@email.com" autoComplete="email"
            value={email} onChange={(e) => setEmail(e.target.value)} required
          />
          <div className="field-help">We&apos;ll always send a code, even if no account exists at that address.</div>
        </div>

        {error && (
          <div className="field-help" style={{ color: "rgb(var(--wo-danger))", marginTop: 8 }}>{error}</div>
        )}

        <button type="submit" className="btn-primary" style={{ marginTop: 8 }} disabled={submitting}>
          {submitting ? "Sending…" : "Send reset code"}
          {SvgSend}
        </button>
      </form>

      <div className="or">or</div>

      <Link to="/sign-in" className="btn-secondary">
        {SvgArrowLeft}
        Try signing in with a code instead
      </Link>

      <p className="reflect">&ldquo;You don&apos;t need to remember everything. Only what matters today.&rdquo;</p>
    </AuthShell>
  );
};
